import { uniq, chunk } from 'lodash';
import pool from '@ricokahler/pool';

// This method breaks up a large request to the API into smaller chunks. This is
// done based on the ids parameter, which takes an array of ids. For example,
// instead of calling the executor function with an array of ids [0..10], using
// batchSize = 2, the executor function will be called 5 times with ids [0,1],
// [1, 2] etc.
//
// This method also prevents multiple identical requests to be made
// simultaneously by keeping track which calls are in progress with which
// arguments.
const batchRequests = ({ batchSize, executor, startLoadingCallback, finishLoadingCallback }) => {
  let pendingRequests = 0;
  const pendingOperations = {};

  const getPendingOperations = id => pendingOperations[id] || (pendingOperations[id] = []);

  const filterInProgress = (ids, args) => ids.filter(id => !getPendingOperations(id).includes(JSON.stringify(args)));

  const registerLoadingInProgress = (ids, args) => {
    for (const id of ids) {
      getPendingOperations(id).push(JSON.stringify(args));
    }
  };

  const registerLoadingComplete = (ids, args) => {
    for (const id of ids) {
      pendingOperations[id] = getPendingOperations(id).filter(e => e !== JSON.stringify(args));
    }
  };

  const cleanup = (ids, args, dispatch) => {
    pendingRequests--;
    if (pendingRequests === 0) {
      // All pending requests are done. Notify the caller
      dispatch(finishLoadingCallback);
    }
    // Set loading state for the combination of ids and args to FALSE
    registerLoadingComplete(ids, args);
  };

  const fetch =
    ({ ids, ...args }) =>
    dispatch => {
      ids = uniq(ids);

      // Ignore all requests that have the same ids and args as a request in
      // progress. This is to reduce redundant requests
      ids = filterInProgress(ids, args);
      if (ids.length === 0) return Promise.resolve();

      // Set loading state for the combination of ids and args to TRUE
      registerLoadingInProgress(ids, args);
      dispatch(startLoadingCallback);

      // Use lodash chunk to break up the requests into batches of `batchSize`.
      const batches = chunk(ids, batchSize);

      const task = ids => {
        pendingRequests++;
        // Call the executor function with the ids corresponding to the chunk
        return dispatch(executor({ ids, ...args }))
          .then(() => {
            cleanup(ids, args, dispatch);
          })
          .catch(() => {
            // Failure: retry by performing the requests one by one
            cleanup(ids, args, dispatch);

            return ids.length <= 1 ? undefined : Promise.all(ids.map(id => dispatch(fetch({ ids: [id], ...args }))));
          });
      };

      return pool({
        collection: batches,
        maxConcurrency: 4,
        task: task
      });
    };

  return fetch;
};

export default batchRequests;
